/**
 * SASSTabBar — Social App Styled Surface bottom tab bar.
 *
 * Phase U0 of the UI/UX roadmap. The ALDS substrate has shipped ~50
 * surfaces (social/messaging/federation/governance/dispatch), but the
 * Hub exposed only 9 of them as reachable entries. SASS gives users a
 * persistent Signal/Telegram-shaped navigation tier so the full feature
 * stack is one tap away from any surface.
 *
 * Four tabs:
 *   - Chats    → sass-chats (threads + DMs + group conversations)
 *   - Feed     → env-social (posts, channels, broadcasts, stories)
 *   - Dispatch → hub (the legacy civic-dispatch role picker)
 *   - More     → sass-more (settings, audit, federation, recovery, plugins)
 *
 * The tab bar is mounted in AppShell and is visible on every unlocked
 * surface — tapping a tab bridges the surface stack to the target
 * surface. The active tab is highlighted based on the current surface,
 * and surfaces that descend from a tab's root (e.g. social-feed under
 * Feed) keep the parent tab highlighted.
 *
 * U0.c+d (2026-05-19): per-tab navigation memory + reactive badges.
 *   - Tapping a tab restores the LAST surface visited within that tab's
 *     family (saved per-tab in localStorage). Tapping the currently-
 *     active tab resets to its root surface.
 *   - Badges count unread items per tab: Chats = unread threads, Feed
 *     = unread mentions, More = pending anchor mismatches. Polled
 *     every 8 seconds.
 */

import React, { useEffect, useState } from 'react';
import type { AppContext } from '../app';

interface Tab {
  readonly id: 'chats' | 'feed' | 'dispatch' | 'more';
  readonly target: string;
  readonly label: string;
  readonly glyph: string;
  readonly match: readonly string[];
}

/**
 * Phase 73 interim (2026-05-24): every registered surface is now mapped to
 * a parent tab so navigating to it highlights a stable tab in the bar.
 * Previously only ~31 of 58 surfaces appeared in any tab's match array,
 * which is what made desktop feel like "only 9 surfaces are reachable"
 * — bridges that landed on an unmapped surface highlighted no tab,
 * leaving users with no breadcrumb back. Coverage is now 58/58 +
 * personal-ai-chat (which is FAB-accessible, intentionally without a
 * tab highlight). `sass-surface-map` (NEW this phase) joins the More tab
 * as the comprehensive discovery drawer.
 */
const TABS: readonly Tab[] = [
  {
    id: 'chats',
    target: 'sass-chats',
    label: 'Chats',
    glyph: '✦',
    match: [
      'sass-chats', 'contact-picker', 'compose-message', 'social-dm',
      // Calls live conceptually with the people you call from Chats.
      'env-voice', 'establish-call', 'call-incoming', 'call-active',
      'call-history', 'media-controls',
      'group-call-incoming', 'group-call-active', 'call-roster',
    ],
  },
  {
    id: 'feed',
    target: 'env-social',
    label: 'Feed',
    glyph: '◐',
    match: [
      'env-social', 'social-feed', 'social-channel', 'social-broadcast',
      'social-stories', 'social-compose', 'social-gallery',
      'social-group-config',
      // Bulletins are the operator-published equivalent of a feed.
      'bulletin-config', 'bulletin-published',
    ],
  },
  {
    id: 'dispatch',
    target: 'hub',
    label: 'Dispatch',
    glyph: '◆',
    match: [
      'hub', 'env-intake', 'env-board', 'env-roster', 'env-audit',
      'env-chat', 'incident-report', 'triage', 'responder-accept',
      'responder-status', 'attach-evidence', 'assign-responder',
      'request-backup', 'share-incident-out',
    ],
  },
  {
    id: 'more',
    target: 'sass-more',
    label: 'More',
    glyph: '≡',
    match: [
      'sass-more', 'sass-surface-map',
      'network-config', 'key-storage-config', 'plugin-marketplace', 'plugin-picker',
      'audit-anchor', 'sass-audit-verify',
      'export-bundle', 'import-bundle',
      'treaty-builder', 'federated-search',
      // Vault is a settings-shaped surface (personal storage).
      'env-vault',
      // Mesh / sneakernet / qr handoff are setup + admin.
      'mesh-invite-generate', 'mesh-invite-redeem', 'sneakernet', 'qr-handoff',
      // SASS extras (moderation, status, recovery wizards).
      'sass-prepublish-vote', 'sass-ble-status', 'sass-sms-subscribers',
      'sass-key-recovery-setup', 'sass-key-recovery-restore',
      // Phase 71 partial — mascot drawer (also reachable via top-right sigil)
      'sass-mascot',
      // Phase 69.A — news-paper digital newspaper (also reachable via top ticker)
      'news-paper',
      // Phase 73 full — shell chooser drawer
      'sass-shell-chooser',
    ],
  },
] as const;

/**
 * Per-tab stack memory. Each tab now remembers not just the LAST
 * surface visited but the entire surface-stack at the moment of
 * navigating away — so Feed → channel → channel-detail → tap Chats →
 * tap Feed returns the user to channel-detail with the channel
 * underneath in the stack (back button works as expected). Without
 * this, the back button after tab-restoration would have nothing to
 * pop.
 */
const TAB_STACK_KEY = 'alds-sass-tab-stacks-v2';

function loadTabStacks(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(TAB_STACK_KEY);
    if (raw === null) return {};
    return JSON.parse(raw) as Record<string, string[]>;
  } catch { return {}; }
}

function saveTabStacks(stacks: Record<string, string[]>): void {
  try { localStorage.setItem(TAB_STACK_KEY, JSON.stringify(stacks)); } catch { /* noop */ }
}

interface Badges {
  chats: number;
  feed: number;
  more: number;
}

async function pollBadges(ctx: AppContext): Promise<Badges> {
  const out: Badges = { chats: 0, feed: 0, more: 0 };
  // Chats — count threads in threads_index with unread messages. The
  // read_state table tracks per-thread last-read; threads with messages
  // newer than that count as unread.
  try {
    const [threads, readRows] = await Promise.all([
      ctx.storage.db.threads_index.toArray(),
      ctx.storage.db.read_state.toArray(),
    ]);
    const readByThread = new Map<string, string>(readRows.map((r) => [(r as { threadId?: string }).threadId ?? '', (r as { lastReadAt?: string }).lastReadAt ?? '']));
    out.chats = threads.filter((t) => {
      const lastRead = readByThread.get(t.threadId) ?? '';
      return (t.lastActivityAt ?? '') > lastRead;
    }).length;
  } catch { /* tables not migrated yet on a brand-new device */ }
  // Feed — count unread mentions across all social groups.
  try {
    const mentions = await ctx.storage.db.social_mentions.toArray();
    out.feed = mentions.filter((m) => (m as { readAt?: string | null }).readAt === null || (m as { readAt?: string | null }).readAt === undefined).length;
  } catch { /* noop */ }
  // More — count anchor mismatches via the AuditVerifier. Lazy-import
  // to keep the tab bar lightweight.
  try {
    const { AuditVerifier } = await import('../governance/AuditVerifier');
    const verifier = new AuditVerifier(ctx);
    const result = await verifier.verifyAnchors();
    out.more = result.mismatches.length;
  } catch { /* noop */ }
  return out;
}

export function SASSTabBar({ ctx }: { ctx: AppContext }): JSX.Element {
  const [activeSurface, setActiveSurface] = useState<string>(ctx.director.state().active ?? 'hub');
  const [tabStacks, setTabStacks] = useState<Record<string, string[]>>(loadTabStacks);
  const [badges, setBadges] = useState<Badges>({ chats: 0, feed: 0, more: 0 });

  // Subscribe to director — track active surface AND record the full
  // current stack as the saved stack for whichever tab family the
  // active surface belongs to.
  useEffect(() => {
    const unsub = ctx.director.subscribe((state) => {
      const next = state.active ?? 'hub';
      setActiveSurface(next);
      const owningTab = TABS.find((t) => t.match.includes(next));
      if (owningTab !== undefined) {
        // Save the full stack — but only entries that belong to this
        // tab's family. Cross-tab leakage (e.g. spawn() pushing a
        // transient surface from another tab) would otherwise pollute
        // the saved stack.
        const filteredStack = state.stack.filter((s) => owningTab.match.includes(s));
        if (filteredStack.length === 0) return;
        setTabStacks((prev) => {
          const existing = prev[owningTab.id];
          if (existing !== undefined && existing.length === filteredStack.length && existing.every((s, i) => s === filteredStack[i])) {
            return prev;  // no change
          }
          const updated = { ...prev, [owningTab.id]: filteredStack };
          saveTabStacks(updated);
          return updated;
        });
      }
    });
    return unsub;
  }, [ctx]);

  // Poll badges periodically. 8 seconds is responsive without thrashing.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const next = await pollBadges(ctx);
      if (!cancelled) setBadges(next);
    };
    void refresh();
    const id = setInterval(() => void refresh(), 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [ctx]);

  const activeTab = TABS.find((t) => t.match.includes(activeSurface))?.id ?? null;

  const handleTab = (tab: Tab): void => {
    // Tapping the currently-active tab: reset to that tab's root.
    if (activeTab === tab.id) {
      ctx.director.bridge(tab.target, `sass:${tab.label.toLowerCase()}:reset`);
      return;
    }
    // Otherwise restore the FULL saved stack for the incoming tab so
    // back-nav works as expected (deepest surface stays at top,
    // shallower ones are reachable via pop). If no stack saved yet,
    // bridge to the tab's root surface.
    const remembered = tabStacks[tab.id];
    if (remembered !== undefined && remembered.length > 0 && remembered.every((s) => tab.match.includes(s))) {
      ctx.director.setStack(remembered, `sass:${tab.label.toLowerCase()}:restore`);
    } else {
      ctx.director.bridge(tab.target, `sass:${tab.label.toLowerCase()}`);
    }
  };

  const badgeFor = (tabId: Tab['id']): number => {
    if (tabId === 'chats') return badges.chats;
    if (tabId === 'feed') return badges.feed;
    if (tabId === 'more') return badges.more;
    return 0;
  };

  return (
    <>
      <nav className="sass-tab-bar" role="tablist" aria-label="ALDS primary navigation">
        {TABS.map((t) => {
          const isActive = activeTab === t.id;
          const count = badgeFor(t.id);
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              aria-label={count > 0 ? `${t.label} (${count} unread)` : t.label}
              className={isActive ? 'sass-tab sass-tab-active' : 'sass-tab'}
              onClick={() => handleTab(t)}
            >
              <span className="sass-tab-glyph" aria-hidden="true">
                {t.glyph}
                {count > 0 && (
                  <span className={t.id === 'more' ? 'sass-tab-badge sass-tab-badge-alert' : 'sass-tab-badge'} aria-hidden="true">
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </span>
              <span className="sass-tab-label">{t.label}</span>
            </button>
          );
        })}
      </nav>
      <style>{styles}</style>
    </>
  );
}

const styles = `
.sass-tab-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  /* Opaque background — must cover any content that scrolls behind. */
  background: #0a0f12;
  border-top: 1px solid var(--rule);
  padding: 6px 4px calc(6px + env(safe-area-inset-bottom)) 4px;
  gap: 2px;
  box-shadow: 0 -8px 16px rgba(0, 0, 0, 0.4);
}

.sass-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--ink-dim);
  font-family: inherit;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 2px;
  transition: color 120ms ease, border-color 120ms ease, background 120ms ease;
  /* U5 — visible focus ring for keyboard nav. Default browser
   * outline is hidden in our overall design but tab navigation needs
   * a clear indicator. */
  outline: none;
}
.sass-tab:focus-visible {
  border-color: var(--phos);
  box-shadow: 0 0 0 2px rgba(127, 255, 159, 0.25);
}
@media (prefers-reduced-motion: reduce) {
  .sass-tab { transition: none; }
}
.sass-tab:hover {
  color: var(--ink);
}
.sass-tab-active {
  color: var(--phos);
  border-color: var(--phos);
  background: rgba(120, 220, 140, 0.06);
}
.sass-tab-glyph {
  font-size: 18px;
  line-height: 1;
  position: relative;
}
.sass-tab-label {
  font-size: 10px;
  line-height: 1;
}

/* Phase U0.d — per-tab badge for unread counts. Phosphor green for
 * informational counts (Chats / Feed); amber-red for alert counts
 * (More / anchor mismatches) since those signal "something needs
 * attention," not just "new content arrived." */
.sass-tab-badge {
  position: absolute;
  top: -6px;
  right: -10px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--phos);
  color: #0a0f12;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  border: 1px solid #0a0f12;
}
.sass-tab-badge-alert {
  background: var(--amber, #f5a623);
  color: #0a0f12;
}

/* Spacer applied to .app-shell so its content isn't hidden behind the bar. */
.app-shell.with-sass-tabs {
  padding-bottom: calc(72px + env(safe-area-inset-bottom));
}

@media (min-width: 900px) {
  /* On desktop the tab bar is still useful but can shrink. */
  .sass-tab-bar { max-width: 600px; margin: 0 auto; border-left: 1px solid var(--rule); border-right: 1px solid var(--rule); }
}
`;
